import React, { useEffect, useRef, useState } from "react";
import { Send, MessagesSquare, Search, ArrowLeft, Video, Plus } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { useAuth } from "../../context/AuthContext";
import { getConversations, adminReply, getMessageContacts, startConversation } from "../../lib/api";
import VideoMeetingModal from "../common/VideoMeetingModal";
import Avatar from "../common/Avatar";

const roleBadge = (r) =>
  ({
    admin: "bg-navy/10 text-navy",
    consultant: "bg-amber-100 text-amber-700",
    client: "bg-green-100 text-green-700",
  }[r] || "bg-gray-100 text-gray-600");

const formatTime = (ts) =>
  new Date(ts).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });

export default function Messages() {
  const { token } = useAuth();
  const [convs, setConvs] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [text, setText] = useState("");
  const [query, setQuery] = useState("");
  const [callOpen, setCallOpen] = useState(false);
  const [newOpen, setNewOpen] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [contactQuery, setContactQuery] = useState("");
  const endRef = useRef(null);

  const refresh = () => {
    if (!token) return;
    getConversations(token)
      .then((list) => {
        setConvs(list);
        setActiveId((cur) => cur || (list.length ? list[0].id : null));
      })
      .catch(() => {});
  };

  useEffect(() => {
    refresh();
    const id = setInterval(refresh, 2500); // poll for new client messages
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const active = activeId ? convs.find((c) => c.id === activeId) : null;
  const callUrl = activeId ? `https://meet.jit.si/RFL-call-${activeId}` : "";

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [active?.messages?.length, activeId]);

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim() || !activeId) return;
    const updated = await adminReply(token, activeId, text.trim());
    setText("");
    setConvs((list) => list.map((c) => (c.id === activeId ? updated : c)));
  };

  const startCall = async () => {
    setCallOpen(true);
    if (activeId) {
      try {
        const updated = await adminReply(token, activeId, `Started a video call: ${callUrl}`);
        setConvs((list) => list.map((c) => (c.id === activeId ? updated : c)));
      } catch (e) {
        /* ignore */
      }
    }
  };

  const openNewMessage = async () => {
    setNewOpen(true);
    try {
      setContacts(await getMessageContacts(token));
    } catch (e) {
      /* ignore */
    }
  };

  const beginConversation = async (userId) => {
    try {
      const conv = await startConversation(token, userId);
      setNewOpen(false);
      setContactQuery("");
      setConvs((list) => {
        const exists = list.some((c) => c.id === conv.id);
        return exists ? list.map((c) => (c.id === conv.id ? conv : c)) : [conv, ...list];
      });
      setActiveId(conv.id);
    } catch (e) {
      /* ignore */
    }
  };

  const filtered = convs.filter((c) =>
    (c.clientName || "").toLowerCase().includes(query.toLowerCase())
  );

  const filteredContacts = contacts.filter(
    (u) =>
      (u.name || "").toLowerCase().includes(contactQuery.toLowerCase()) ||
      (u.email || "").toLowerCase().includes(contactQuery.toLowerCase())
  );

  const lastMsg = (c) =>
    c.messages.length ? c.messages[c.messages.length - 1] : null;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="font-serif text-2xl font-bold text-navy">Messages</h1>
          <p className="text-gray-500 text-sm mt-0.5">
            Chat directly with your clients · {convs.length} conversations
          </p>
        </div>
        <Button onClick={openNewMessage} className="bg-navy hover:bg-navy-light text-white" data-testid="new-message-button">
          <Plus className="h-4 w-4 mr-1.5" /> New Message
        </Button>
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden grid md:grid-cols-[320px_1fr] h-[calc(100vh-200px)] min-h-[480px]">
        {/* Conversation list */}
        <div className={`border-r border-gray-100 min-w-0 flex-col ${activeId ? "hidden md:flex" : "flex"}`}>
          <div className="p-3 border-b border-gray-100">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search clients..."
                className="pl-10 h-9"
              />
            </div>
          </div>
          <div className="flex-1 overflow-y-auto">
            {filtered.map((c) => {
              const lm = lastMsg(c);
              const isActive = c.id === activeId;
              return (
                <button
                  key={c.id}
                  onClick={() => setActiveId(c.id)}
                  className={`w-full text-left px-4 py-3 flex items-start gap-3 border-b border-gray-50 transition-colors ${
                    isActive ? "bg-cream" : "hover:bg-cream/50"
                  }`}
                >
                  <Avatar
                    name={c.clientName}
                    src={c.clientAvatar}
                    className="h-10 w-10 rounded-full bg-navy text-gold text-sm font-semibold flex-shrink-0"
                  />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-medium text-navy text-sm truncate">{c.clientName}</span>
                      {lm && <span className="text-[10px] text-gray-400 flex-shrink-0">{formatTime(lm.ts)}</span>}
                    </div>
                    <div className="text-xs text-gray-500 truncate mt-0.5">
                      {lm ? (lm.sender === "admin" ? "You: " : "") + lm.text : "No messages yet"}
                    </div>
                  </div>
                </button>
              );
            })}
            {filtered.length === 0 && (
              <p className="text-sm text-gray-400 text-center py-10">No conversations.</p>
            )}
          </div>
        </div>

        {/* Thread */}
        <div className={`flex-col min-w-0 ${activeId ? "flex" : "hidden md:flex"}`}>
          {active ? (
            <>
              <div className="px-5 py-3.5 border-b border-gray-100 flex items-center gap-3">
                <button onClick={() => setActiveId(null)} className="md:hidden text-navy">
                  <ArrowLeft className="h-5 w-5" />
                </button>
                <Avatar
                  name={active.clientName}
                  src={active.clientAvatar}
                  className="h-10 w-10 rounded-full bg-navy text-gold text-sm font-semibold"
                />
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-navy text-sm truncate">{active.clientName}</div>
                  <div className="text-xs text-gray-400 truncate">{active.clientEmail}</div>
                </div>
                <Button onClick={startCall} className="bg-gold hover:bg-gold-dark text-navy font-semibold h-9">
                  <Video className="h-4 w-4 sm:mr-1.5" /> <span className="hidden sm:inline">Video Call</span>
                </Button>
              </div>

              <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-5 space-y-4 bg-cream/40">
                {active.messages.map((m) => {
                  const mine = m.sender === "admin";
                  return (
                    <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                      <div className={`max-w-[78%] flex flex-col ${mine ? "items-end" : "items-start"}`}>
                        <div
                          className={`px-4 py-2.5 rounded-2xl text-sm leading-relaxed break-words max-w-full ${
                            mine
                              ? "bg-navy text-white rounded-br-md"
                              : "bg-white border border-gray-100 text-gray-700 rounded-bl-md shadow-sm"
                          }`}
                        >
                          {m.text}
                        </div>
                        <span className="text-[11px] text-gray-400 mt-1 px-1">
                          {mine ? "You" : (active.clientName || "Client").split(" ")[0]} · {formatTime(m.ts)}
                        </span>
                      </div>
                    </div>
                  );
                })}
                <div ref={endRef} />
              </div>

              <form onSubmit={send} className="p-3 sm:p-4 border-t border-gray-100 flex items-center gap-2">
                <Input
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  placeholder="Type your reply..."
                  className="flex-1"
                />
                <Button type="submit" className="bg-navy hover:bg-navy-light text-white h-10 px-4">
                  <Send className="h-4 w-4" />
                </Button>
              </form>
            </>
          ) : (
            <div className="flex-1 flex flex-col items-center justify-center text-gray-400">
              <MessagesSquare className="h-12 w-12 mb-3" />
              Select a conversation to start chatting.
            </div>
          )}
        </div>
      </div>

      <VideoMeetingModal
        open={callOpen}
        onOpenChange={setCallOpen}
        url={callUrl}
        title={active ? `Call with ${active.clientName}` : "Video Call"}
        name="RFL Coach"
      />

      <Dialog open={newOpen} onOpenChange={setNewOpen}>
        <DialogContent className="sm:max-w-md" data-testid="new-message-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-navy">New Message</DialogTitle>
          </DialogHeader>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              value={contactQuery}
              onChange={(e) => setContactQuery(e.target.value)}
              placeholder="Search people..."
              className="pl-10"
              data-testid="contact-search-input"
            />
          </div>
          <div className="max-h-80 overflow-y-auto -mx-2 mt-1">
            {filteredContacts.map((u) => (
              <button
                key={u.id}
                onClick={() => beginConversation(u.id)}
                className="w-full text-left px-3 py-2.5 flex items-center gap-3 rounded-lg hover:bg-cream transition-colors"
                data-testid={`contact-${u.id}`}
              >
                <Avatar
                  name={u.name}
                  src={u.avatar}
                  className="h-9 w-9 rounded-full bg-navy text-gold text-sm font-semibold flex-shrink-0"
                />
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-navy truncate">{u.name || "Unnamed"}</div>
                  <div className="text-xs text-gray-400 truncate">{u.email}</div>
                </div>
                <span className={`text-[10px] px-2 py-0.5 rounded-full capitalize ${roleBadge(u.role)}`}>{u.role}</span>
                {u.hasConversation && <span className="text-[10px] text-gray-400">existing</span>}
              </button>
            ))}
            {filteredContacts.length === 0 && (
              <p className="text-sm text-gray-400 text-center py-8">No people found.</p>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
